import React, { useRef, useState } from "react";
import { Type, Link2, Video, Paperclip, Trash2, Upload } from "lucide-react";
import { api } from "../lib/api";

// File links are stored as relative "/api/..." paths; keep them relative so
// they stay same-origin on whatever host the page is served from.
export const fileHref = (url) => url || "";

function embedUrl(url) {
  const m = (url || "").match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return url;
}

export function BlockRender({ block }) {
  if (block.type === "text")
    return <div className="whitespace-pre-line font-light leading-relaxed text-ink-soft">{block.value}</div>;
  if (block.type === "link")
    return (
      <a href={block.url} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-lg border border-bordeaux-border bg-bordeaux-tint/40 px-4 py-2.5 text-sm font-medium text-bordeaux transition hover:bg-bordeaux-tint">
        <Link2 size={15} /> {block.label || block.url}
      </a>
    );
  if (block.type === "video")
    return (
      <div className="aspect-video overflow-hidden rounded-2xl border border-line bg-black">
        <iframe src={embedUrl(block.url)} title="Vidéo" className="h-full w-full" allowFullScreen frameBorder="0" />
      </div>
    );
  if (block.type === "file")
    return (
      <a href={fileHref(block.url)} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-2 rounded-lg border border-line bg-white px-4 py-2.5 text-sm font-medium text-bordeaux transition hover:bg-bordeaux-tint/40">
        <Paperclip size={15} /> {block.name || "Télécharger le fichier"}
      </a>
    );
  return null;
}

export function BlocksView({ blocks }) {
  return <div className="space-y-4">{(blocks || []).map((b, i) => <BlockRender key={i} block={b} />)}</div>;
}

export function BlockComposer({ blocks, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const list = blocks || [];
  const add = (b) => onChange([...list, b]);
  const update = (i, patch) => onChange(list.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      add({ type: "file", url: data.url, name: data.name });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div className="space-y-3">
      <div className="space-y-3">
        {list.map((b, i) => (
          <div key={i} className="rounded-xl border border-line bg-cream/40 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{b.type}</span>
              <button type="button" data-testid={`composer-remove-${i}`} onClick={() => remove(i)} className="text-ink-muted hover:text-bordeaux"><Trash2 size={15} /></button>
            </div>
            {b.type === "text" && (
              <textarea data-testid={`composer-text-${i}`} value={b.value} onChange={(e) => update(i, { value: e.target.value })} rows={4}
                placeholder="Ton texte…" className="w-full rounded-lg border border-line bg-white p-3 text-sm outline-none focus:border-bordeaux" />
            )}
            {b.type === "link" && (
              <div className="space-y-2">
                <input value={b.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Libellé du bouton" className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-bordeaux" />
                <input value={b.url} onChange={(e) => update(i, { url: e.target.value })} placeholder="https://…" className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-bordeaux" />
              </div>
            )}
            {b.type === "video" && (
              <input value={b.url} onChange={(e) => update(i, { url: e.target.value })} placeholder="Lien YouTube / Loom" className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-bordeaux" />
            )}
            {b.type === "file" && (
              <p className="flex items-center gap-2 text-sm text-ink-soft"><Paperclip size={14} /> {b.name}</p>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" data-testid="composer-add-text" onClick={() => add({ type: "text", value: "" })} className="flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs text-ink-soft hover:border-bordeaux hover:text-bordeaux"><Type size={14} /> Texte</button>
        <button type="button" data-testid="composer-add-link" onClick={() => add({ type: "link", label: "", url: "" })} className="flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs text-ink-soft hover:border-bordeaux hover:text-bordeaux"><Link2 size={14} /> Lien</button>
        <button type="button" data-testid="composer-add-video" onClick={() => add({ type: "video", url: "" })} className="flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs text-ink-soft hover:border-bordeaux hover:text-bordeaux"><Video size={14} /> Vidéo</button>
        <button type="button" data-testid="composer-add-file" disabled={uploading} onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-xs text-ink-soft hover:border-bordeaux hover:text-bordeaux disabled:opacity-50"><Upload size={14} /> {uploading ? "Envoi…" : "Fichier"}</button>
        <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid="composer-file-input" />
      </div>
    </div>
  );
}
